import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { message, Modal } from 'antd';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import Header from '../Header';
import Sidebar from '../Sidebar';
import teleChatApi from '../../api/telechat.api';
import { useWebSocket } from '../../contexts/WebSocketContext';
import ConnectionStatus from './ConnectionStatus';
import { Card, StatusPill, EmptyState } from '../../ui';

// Status → design-system tone + Bulgarian label (shared with the patient screens)
const STATUS = {
  PENDING_APPROVAL: { tone: 'warning', label: 'Чака одобрение' },
  ACTIVE:           { tone: 'success', label: 'Активна' },
  CLOSED:           { tone: 'neutral', label: 'Приключена' },
  DECLINED:         { tone: 'danger',  label: 'Отказана' },
};

const FILTERS = [
  { value: 'PENDING_APPROVAL', label: 'Чакащи одобрение' },
  { value: 'ACTIVE', label: 'Активни' },
  { value: 'CLOSED', label: 'Приключени' },
  { value: 'DECLINED', label: 'Отказани' },
];

// Doctor inbox ("Консултации") styling — mirrors the patient lobby with the
// doctor-specific bits: status filters, search, pending highlight, close action.
const LOBBY_CSS = `
  .vc-page {
    font-family: var(--vc-font-sans); color: var(--vc-color-text);
    padding: var(--vc-space-5); width: 100%;
    max-width: var(--vc-container); margin: 0 auto;
    display: flex; flex-direction: column; gap: var(--vc-space-4);
  }

  /* ---- header row ---- */
  .lb-head { display: flex; align-items: center; gap: var(--vc-space-3); flex-wrap: wrap; }
  .lb-titlewrap { flex: 1 1 auto; min-width: 0; display: flex; align-items: center; gap: var(--vc-space-3); }
  .lb-titleicon { flex: none; width: 42px; height: 42px; border-radius: var(--vc-radius-lg); background: var(--vc-color-primary-soft); color: var(--vc-color-primary); display: grid; place-items: center; }
  .lb-title { margin: 0; font-size: var(--vc-text-2xl); font-weight: var(--vc-weight-semibold); line-height: var(--vc-leading-tight); }
  .lb-pending-badge { display: inline-grid; place-items: center; min-width: 22px; height: 22px; padding: 0 7px; margin-left: var(--vc-space-2); border-radius: var(--vc-radius-pill); background: var(--vc-color-warning); color: #fff; font-family: var(--vc-font-mono); font-size: var(--vc-text-xs); font-weight: var(--vc-weight-bold); vertical-align: middle; }
  .lb-subtitle { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); margin-top: 2px; }
  .lb-actions { display: flex; align-items: center; gap: var(--vc-space-2); flex: none; }

  .lb-icon-btn { appearance: none; cursor: pointer; flex: none; display: inline-grid; place-items: center; width: 42px; height: 42px; color: var(--vc-color-text-muted); background: var(--vc-color-surface); border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-md); transition: color .12s ease, border-color .12s ease; }
  .lb-icon-btn:hover { color: var(--vc-color-primary); border-color: var(--vc-color-primary); }

  /* ---- search ---- */
  .lb-search { position: relative; display: flex; align-items: center; }
  .lb-search .vc-ico { position: absolute; left: var(--vc-space-3); display: inline-flex; color: var(--vc-color-text-subtle); pointer-events: none; }
  .lb-search input { width: 220px; max-width: 100%; font-family: inherit; font-size: var(--vc-text-base); color: var(--vc-color-text); padding: 9px var(--vc-space-3) 9px calc(var(--vc-space-3)*2 + 16px); border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-md); background: var(--vc-color-surface); outline: none; }
  .lb-search input::placeholder { color: var(--vc-color-text-subtle); }
  .lb-search input:focus { border-color: var(--vc-color-primary); box-shadow: 0 0 0 3px var(--vc-color-primary-soft); }

  /* ---- status filters ---- */
  .lb-filters { display: flex; flex-wrap: wrap; gap: var(--vc-space-2); }
  .lb-filter { appearance: none; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-family: inherit; font-size: var(--vc-text-sm); font-weight: var(--vc-weight-medium); padding: 6px 14px; border-radius: var(--vc-radius-pill); border: 1px solid var(--vc-color-border); background: var(--vc-color-surface); color: var(--vc-color-text-muted); transition: all .12s ease; user-select: none; }
  .lb-filter:hover { border-color: var(--vc-color-primary); color: var(--vc-color-primary); }
  .lb-filter.active { background: var(--vc-color-primary); border-color: var(--vc-color-primary); color: #fff; }

  /* ---- desktop table ---- */
  .lb-table { width: 100%; border-collapse: collapse; }
  .lb-table thead th { text-align: left; font-size: var(--vc-text-xs); font-weight: var(--vc-weight-semibold); text-transform: uppercase; letter-spacing: .05em; color: var(--vc-color-text-subtle); padding: 0 var(--vc-space-3) var(--vc-space-2); border-bottom: 1px solid var(--vc-color-border); }
  .lb-row { cursor: pointer; transition: background .12s ease; }
  .lb-row:hover { background: var(--vc-color-primary-soft); }
  .lb-row.pending { background: var(--vc-color-warning-bg); }
  .lb-row.pending:hover { background: #fdecc4; }
  .lb-row > td { padding: var(--vc-space-3); border-bottom: 1px solid var(--vc-color-border-soft); vertical-align: middle; }
  .lb-row:last-child > td { border-bottom: none; }

  .lb-person-cell { display: flex; align-items: center; gap: var(--vc-space-3); min-width: 0; }
  .lb-avatar { flex: none; width: 40px; height: 40px; border-radius: var(--vc-radius-pill); background: var(--vc-color-primary); color: #fff; display: grid; place-items: center; font-weight: var(--vc-weight-bold); font-size: var(--vc-text-md); }
  .lb-person-name { font-size: var(--vc-text-md); font-weight: var(--vc-weight-semibold); color: var(--vc-color-primary); line-height: var(--vc-leading-tight); }
  .lb-row:hover .lb-person-name { text-decoration: underline; }
  .lb-time { font-family: var(--vc-font-mono); font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); white-space: nowrap; }
  .lb-last { display: flex; align-items: center; gap: var(--vc-space-2); min-width: 0; max-width: 320px; }
  .lb-last-text { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
  .lb-row.unread .lb-last-text { color: var(--vc-color-text); font-weight: var(--vc-weight-medium); }
  .unread-badge { flex: none; display: inline-grid; place-items: center; min-width: 20px; height: 20px; padding: 0 6px; border-radius: var(--vc-radius-pill); background: var(--vc-color-primary); color: #fff; font-family: var(--vc-font-mono); font-size: var(--vc-text-xs); font-weight: var(--vc-weight-bold); line-height: 1; }
  .lb-actions-cell { display: flex; align-items: center; gap: var(--vc-space-2); justify-content: flex-end; color: var(--vc-color-text-subtle); }

  .lb-close { appearance: none; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-family: inherit; font-size: var(--vc-text-sm); font-weight: var(--vc-weight-medium); padding: 5px 12px; border-radius: var(--vc-radius-md); border: 1px solid var(--vc-color-border); background: var(--vc-color-surface); color: var(--vc-color-text-muted); }
  .lb-close:hover { border-color: var(--vc-color-danger); color: var(--vc-color-danger); background: var(--vc-color-danger-bg); }

  /* ---- mobile cards ---- */
  .lb-cards { display: flex; flex-direction: column; gap: var(--vc-space-3); }
  .lb-card-link { cursor: pointer; display: block; }
  .lb-card { display: flex; flex-direction: column; gap: var(--vc-space-3); }
  .lb-card.pending { box-shadow: inset 3px 0 0 var(--vc-color-warning); padding-left: var(--vc-space-2); }
  .lb-card-top { display: flex; align-items: center; gap: var(--vc-space-3); }
  .lb-card .lb-avatar { width: 46px; height: 46px; }
  .lb-card-id { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
  .lb-card-name { font-size: var(--vc-text-md); font-weight: var(--vc-weight-semibold); color: var(--vc-color-primary); line-height: var(--vc-leading-tight); overflow-wrap: anywhere; }
  .lb-card-meta { display: flex; align-items: center; gap: var(--vc-space-2) var(--vc-space-3); flex-wrap: wrap; }
  .lb-card-msg { display: flex; align-items: center; gap: var(--vc-space-2); padding: var(--vc-space-2) var(--vc-space-3); background: var(--vc-color-surface-muted); border: 1px solid var(--vc-color-border-soft); border-radius: var(--vc-radius-md); }
  .lb-card-msg .txt { font-size: var(--vc-text-sm); color: var(--vc-color-text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; flex: 1 1 auto; }
  .lb-card.unread .lb-card-msg .txt { color: var(--vc-color-text); font-weight: var(--vc-weight-medium); }

  /* ---- loading skeleton ---- */
  .sk { background: linear-gradient(90deg, var(--vc-color-border-soft) 25%, var(--vc-color-surface-muted) 50%, var(--vc-color-border-soft) 75%); background-size: 200% 100%; animation: sk 1.3s ease infinite; border-radius: var(--vc-radius-sm); }
  @keyframes sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
  .sk-row { display: flex; align-items: center; gap: var(--vc-space-3); padding: var(--vc-space-3); border-bottom: 1px solid var(--vc-color-border-soft); }
  .sk-row:last-child { border-bottom: none; }
  .sk-avatar { flex: none; width: 40px; height: 40px; border-radius: var(--vc-radius-pill); }
  .sk-lines { flex: 1 1 auto; display: flex; flex-direction: column; gap: 7px; }

  /* ---- responsive: table on desktop, cards on mobile ---- */
  .lb-desktop { display: block; }
  .lb-mobile { display: none; }
  @media (max-width: 768px) {
    .lb-desktop { display: none; }
    .lb-mobile { display: block; }
    .lb-title { font-size: var(--vc-text-xl); }
    .lb-actions { width: 100%; }
    .lb-search { flex: 1 1 auto; }
    .lb-search input { width: 100%; }
  }
`;

const initial = (name) => (name ? name.trim().charAt(0).toUpperCase() : 'П');

const DoctorChatList = () => {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [closeModalVisible, setCloseModalVisible] = useState(false);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const { subscribeToLobbyUpdates } = useWebSocket();

  // Subscribe to WebSocket lobby updates
  useEffect(() => {
    const unsubscribe = subscribeToLobbyUpdates((event) => {
      if (event.type === 'LOBBY_UPDATE') {
        setChats((prev) => {
          const index = prev.findIndex((c) => c.chatId === event.chatId);
          if (index === -1) {
            if (statusFilter.length === 0 || statusFilter.includes(event.payload.status)) {
              return [event.payload, ...prev];
            }
            return prev;
          }
          const updated = [...prev];
          updated[index] = event.payload;
          return updated;
        });
      }
    });
    return () => unsubscribe();
  }, [subscribeToLobbyUpdates, statusFilter]);

  const toggleStatusFilter = (value) => {
    setStatusFilter((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  useEffect(() => {
    loadChats();
  }, [statusFilter]);

  // Refetch when the page becomes visible again (phone unlock / app switch)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) loadChats();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [statusFilter, search]);

  const loadChats = async () => {
    try {
      setLoading(true);
      const data = await teleChatApi.getDoctorChats(statusFilter, search || undefined);
      setChats(data);
    } catch (error) {
      console.error('Error loading chats:', error);
      message.error('Грешка при зареждане на консултациите');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') loadChats();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    const date = new Date(utcDateString);
    const datePart = date.toLocaleDateString('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Sofia' });
    const timePart = date.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Sofia' });
    return `${datePart} ${timePart}`;
  };

  const handleChatClick = (chat) => navigate(`/doctor/telechat/${chat.chatId}`);

  const handleCloseClick = (e, chatId) => {
    e.stopPropagation();
    setSelectedChatId(chatId);
    setCloseModalVisible(true);
  };

  const handleClose = async () => {
    if (!selectedChatId) return;
    try {
      setActionLoading(true);
      await teleChatApi.closeChat(selectedChatId);
      message.success('Консултацията е приключена');
      setCloseModalVisible(false);
      setSelectedChatId(null);
      loadChats();
    } catch (error) {
      console.error('Error closing chat:', error);
      message.error('Грешка при приключване на консултацията');
    } finally {
      setActionLoading(false);
    }
  };

  const pendingCount = chats.filter((c) => c.status === 'PENDING_APPROVAL').length;
  const subtitle = chats.length > 0
    ? `${chats.length} ${chats.length === 1 ? 'консултация' : 'консултации'}`
    : 'Телемедицински консултации';

  const renderTableRow = (chat) => {
    const s = STATUS[chat.status] || { tone: 'neutral', label: chat.status };
    const pending = chat.status === 'PENDING_APPROVAL';
    const name = chat.patient?.fullName || 'Неизвестен пациент';
    return (
      <tr
        key={chat.chatId}
        className={'lb-row' + (pending ? ' pending' : '') + (chat.unreadCount > 0 ? ' unread' : '')}
        onClick={() => handleChatClick(chat)}
      >
        <td>
          <div className="lb-person-cell">
            <span className="lb-avatar">{initial(chat.patient?.fullName)}</span>
            <span className="lb-person-name">{name}</span>
          </div>
        </td>
        <td><StatusPill tone={s.tone} size="sm">{s.label}</StatusPill></td>
        <td><span className="lb-time">{formatDate(chat.createdAt)}</span></td>
        <td>
          <div className="lb-last">
            <span className="lb-last-text">{chat.lastMessage || 'Няма съобщения'}</span>
            {chat.unreadCount > 0 && <span className="unread-badge">{chat.unreadCount}</span>}
          </div>
        </td>
        <td>
          <div className="lb-actions-cell">
            {chat.status === 'ACTIVE' && (
              <button className="lb-close" onClick={(e) => handleCloseClick(e, chat.chatId)}>
                <FeatherIcon icon="x-circle" size={14} /> Приключи
              </button>
            )}
            <FeatherIcon icon="chevron-right" size={18} />
          </div>
        </td>
      </tr>
    );
  };

  const renderCard = (chat) => {
    const s = STATUS[chat.status] || { tone: 'neutral', label: chat.status };
    const pending = chat.status === 'PENDING_APPROVAL';
    const name = chat.patient?.fullName || 'Неизвестен пациент';
    return (
      <div key={chat.chatId} className="lb-card-link" onClick={() => handleChatClick(chat)}>
        <Card flat padding="compact">
          <div className={'lb-card' + (pending ? ' pending' : '') + (chat.unreadCount > 0 ? ' unread' : '')}>
            <div className="lb-card-top">
              <span className="lb-avatar">{initial(chat.patient?.fullName)}</span>
              <div className="lb-card-id">
                <span className="lb-card-name">{name}</span>
                <div className="lb-card-meta">
                  <StatusPill tone={s.tone} size="sm">{s.label}</StatusPill>
                  <span className="lb-time">{formatDate(chat.createdAt)}</span>
                </div>
              </div>
            </div>
            {chat.lastMessage && (
              <div className="lb-card-msg">
                <span className="txt">{chat.lastMessage}</span>
                {chat.unreadCount > 0 && <span className="unread-badge">{chat.unreadCount}</span>}
              </div>
            )}
            {chat.status === 'ACTIVE' && (
              <button className="lb-close" style={{ alignSelf: 'flex-start' }} onClick={(e) => handleCloseClick(e, chat.chatId)}>
                <FeatherIcon icon="x-circle" size={14} /> Приключи
              </button>
            )}
          </div>
        </Card>
      </div>
    );
  };

  const renderSkeleton = (rows) => (
    <div>
      {Array.from({ length: rows }).map((_, i) => (
        <div className="sk-row" key={i}>
          <span className="sk sk-avatar" />
          <span className="sk-lines">
            <span className="sk" style={{ height: 12, width: '40%' }} />
            <span className="sk" style={{ height: 10, width: '65%' }} />
          </span>
          <span className="sk" style={{ height: 22, width: 84, borderRadius: 'var(--vc-radius-pill)' }} />
        </div>
      ))}
    </div>
  );

  const renderBody = () => {
    if (loading) {
      return <Card padding="comfortable">{renderSkeleton(5)}</Card>;
    }
    if (chats.length === 0) {
      return (
        <Card padding="comfortable">
          <EmptyState
            icon={<FeatherIcon icon="message-circle" size={28} />}
            title="Няма консултации"
            description={`Няма консултации${statusFilter.length > 0 ? ' с избраните филтри' : ''}.`}
          />
        </Card>
      );
    }
    return (
      <>
        <div className="lb-desktop">
          <Card padding="flush">
            <div style={{ padding: 'var(--vc-space-2)' }}>
              <table className="lb-table">
                <thead>
                  <tr>
                    <th>Пациент</th>
                    <th>Статус</th>
                    <th>Начало</th>
                    <th>Последно съобщение</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>{chats.map(renderTableRow)}</tbody>
              </table>
            </div>
          </Card>
        </div>
        <div className="lb-mobile">
          <div className="lb-cards">{chats.map(renderCard)}</div>
        </div>
      </>
    );
  };

  return (
    <div>
      <Header />
      <Sidebar activeClassName="doctor-telechat" />
      <style>{LOBBY_CSS}</style>

      <div className="page-wrapper">
        <div className="content" style={{ padding: 0 }}>
          <div className="vc-page">
            {/* Header */}
            <div className="lb-head">
              <div className="lb-titlewrap">
                <span className="lb-titleicon"><FeatherIcon icon="message-square" size={22} /></span>
                <div style={{ minWidth: 0 }}>
                  <h1 className="lb-title">
                    Консултации
                    {pendingCount > 0 && <span className="lb-pending-badge">{pendingCount}</span>}
                  </h1>
                  <div className="lb-subtitle">{subtitle}</div>
                </div>
              </div>
              <div className="lb-actions">
                <div className="lb-search">
                  <span className="vc-ico"><FeatherIcon icon="search" size={16} /></span>
                  <input
                    type="search"
                    placeholder="Търси пациент..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                  />
                </div>
                <button className="lb-icon-btn" onClick={loadChats} title="Обнови" aria-label="Обнови">
                  <FeatherIcon icon="refresh-cw" size={17} />
                </button>
              </div>
            </div>

            {/* Status filters */}
            <div className="lb-filters">
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className={'lb-filter' + (statusFilter.includes(f.value) ? ' active' : '')}
                  onClick={() => toggleStatusFilter(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <ConnectionStatus />

            {renderBody()}
          </div>
        </div>
      </div>

      {/* Close Modal */}
      <Modal
        title={
          <span>
            <FeatherIcon icon="check-circle" size={16} style={{ color: '#2e37a4', marginRight: 8, verticalAlign: 'middle' }} />
            Приключи консултацията
          </span>
        }
        open={closeModalVisible}
        onOk={handleClose}
        onCancel={() => {
          setCloseModalVisible(false);
          setSelectedChatId(null);
        }}
        okText="Приключи"
        cancelText="Назад"
        confirmLoading={actionLoading}
      >
        <p>Сигурни ли сте, че искате да приключите тази консултация?</p>
      </Modal>
    </div>
  );
};

export default DoctorChatList;
